import React, { useState, useRef } from "react";
import Main from "../Main";
import AppBreadcrumbs from "../../components/ui/AppBreadcrumbs";
import {
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { aepsAuthanticateCheck } from "../../api/aepsApi";
import { getLocation } from "../../components/Navigator/Navigator";
import { useInactivityTimeout } from "../../hooks/useInactivityTimeout";
import { useNavigate } from "react-router-dom";
import FingerprintCapture from "../../hooks/FingerprintCapture";
import { useNotify } from "../../context/NotifyContext";

const Authanticate = () => {
  const [openAuthPopup, setOpenAuthPopup] = useState(true);
  const [pidData, setPidData] = useState("");
  const [form, setForm] = useState({ aadhaar: "", mobile: "" });
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const fetchedRef = useRef(false);

  const navigate = useNavigate();
  const { notify } = useNotify(); // ✅ secure notification

  // 🔐 Clear everything
  const clearSession = () => {
    setForm({ aadhaar: "", mobile: "" });
    setPidData("");
    setUserData(null);
    fetchedRef.current = false;
  };

  // ⏳ Auto session expire (10 min)
  useInactivityTimeout(() => {
    if (userData) {
      clearSession();
      notify("Session expired due to inactivity", "info");
    }
  }, 10 * 60 * 1000);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    if ((name === "aadhaar" || name === "mobile") && !/^\d*$/.test(value)) {
      return; // numeric only
    }
    setForm({ ...form, [name]: value });
  };

  // 👉 SUBMIT AUTH CHECK
  const submit = async (e) => {
    e.preventDefault();
    const { aadhaar, mobile } = form;

    if (aadhaar.length !== 12) return notify("Invalid Aadhaar number", "error");
    if (mobile.length !== 10) return notify("Invalid mobile number", "error");
    if (!pidData) return notify("Fingerprint is required", "error");
    if (fetchedRef.current) return; // prevent duplicate submit

    setLoading(true);
    try {
      const location = await getLocation(); // 🔐 secure location fetch

      const payload = {
        aadhaar,
        mobile,
        pidData,
        latitude: location.latitude,
        longitude: location.longitude,
      };

      const result = await aepsAuthanticateCheck(payload);

      if (result?.data?.status) {
        fetchedRef.current = true; // prevent multiple hits
        navigate("/aeps/balance-enquiry");
        notify(result?.data?.message || "Authenticated successfully", "success");
      } else {
        notify(result?.data?.message || "Authentication failed", "error");
      }
    } catch (err) {
      console.error("Auth check error:", err);
      notify("Server error, please try again", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Main showRightPanel={false}>
      <AppBreadcrumbs
        items={[
          { label: "Dashboard", to: "/" },
          { label: "Aadhaar" },
          { label: "Auth Check" },
        ]}
      />

      <Dialog open={openAuthPopup} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, textAlign: "center" }}>Auth Check</DialogTitle>
        <DialogContent>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <TextField
              label="Aadhaar Number"
              name="aadhaar"
              fullWidth
              required
              inputProps={{ maxLength: 12 }}
              value={form.aadhaar}
              onChange={handleChange}
            />
            <TextField
              label="Mobile Number"
              name="mobile"
              fullWidth
              required
              inputProps={{ maxLength: 10 }}
              value={form.mobile}
              onChange={handleChange}
            />

            <FingerprintCapture
              onSuccess={(pid) => setPidData(pid)}
              onError={() => setPidData("")}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ backgroundColor: "blue", padding: "10px", borderRadius: "8px" }}
            >
              {loading ? <CircularProgress size={22} /> : "Authenticate"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </Main>
  );
};

export default Authanticate;
